import { useEffect, useState, useCallback } from "react";
import { fetchParts, createPart, updatePart, deletePart } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/api";

const empty = { nome: "", codigo: "", descricao: "", preco_unitario: 0, quantidade_estoque: 0, estoque_minimo: 5 };

export default function Inventory() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const reload = useCallback(() => fetchParts().then(setList), []);
  useEffect(() => { reload(); }, [reload]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome) { toast.error("Nome é obrigatório"); return; }
    const payload = {
      ...form,
      preco_unitario: parseFloat(form.preco_unitario || 0),
      quantidade_estoque: parseInt(form.quantidade_estoque || 0, 10),
      estoque_minimo: parseInt(form.estoque_minimo || 0, 10),
    };
    try {
      if (editId) await updatePart(editId, payload);
      else await createPart(payload);
      toast.success("Peça salva");
      setOpen(false); setForm(empty); setEditId(null);
      reload();
    } catch { toast.error("Erro ao salvar"); }
  };

  const onEdit = (p) => {
    setEditId(p.id);
    setForm({ ...p });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir esta peça?")) return;
    await deletePart(id);
    toast.success("Peça excluída");
    reload();
  };

  return (
    <div data-testid="inventory-page">
      <PageHeader
        title="Estoque de Peças"
        subtitle="Controle do inventário com alerta de estoque mínimo."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-part-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">
                <Plus size={16} weight="bold" className="mr-1.5" /> Nova Peça
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[560px]">
              <DialogHeader>
                <DialogTitle className="font-heading">{editId ? "Editar Peça" : "Nova Peça"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nome *</Label>
                    <Input data-testid="part-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                  </div>
                  <div>
                    <Label>Código (SKU)</Label>
                    <Input data-testid="part-codigo" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
                  </div>
                  <div>
                    <Label>Preço Unitário (R$)</Label>
                    <Input data-testid="part-preco" type="number" step="0.01" value={form.preco_unitario} onChange={(e) => setForm({ ...form, preco_unitario: e.target.value })} />
                  </div>
                  <div>
                    <Label>Qtd. em Estoque</Label>
                    <Input data-testid="part-qtd" type="number" value={form.quantidade_estoque} onChange={(e) => setForm({ ...form, quantidade_estoque: e.target.value })} />
                  </div>
                  <div>
                    <Label>Estoque Mínimo</Label>
                    <Input data-testid="part-min" type="number" value={form.estoque_minimo} onChange={(e) => setForm({ ...form, estoque_minimo: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <Label>Descrição</Label>
                    <Textarea data-testid="part-desc" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-part-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">Salvar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[#E2E8F0] rounded-md">
        {list.length === 0 ? (
          <EmptyState title="Nenhuma peça cadastrada" message="Adicione peças ao seu inventário." />
        ) : (
          <Table>
            <TableHeader className="bg-[#F8FAFC]">
              <TableRow>
                <TableHead>Peça</TableHead>
                <TableHead>Código</TableHead>
                <TableHead className="text-right">Preço</TableHead>
                <TableHead className="text-right">Estoque</TableHead>
                <TableHead className="text-right">Mínimo</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((p, i) => {
                const low = p.quantidade_estoque <= p.estoque_minimo;
                return (
                  <TableRow key={p.id} className={i % 2 ? "bg-[#F8FAFC]" : ""} data-testid={`part-row-${p.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {low && <Warning size={14} weight="fill" className="text-red-500" />}
                        {p.nome}
                      </div>
                      {p.descricao && <div className="text-xs text-[#475569] mt-0.5">{p.descricao}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[#475569]">{p.codigo || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoney(p.preco_unitario)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={low ? "chip chip-low" : "chip chip-paid"}>{p.quantidade_estoque}</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-[#475569]">{p.estoque_minimo}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(p)} data-testid={`edit-part-${p.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDelete(p.id)} data-testid={`delete-part-${p.id}`}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
